// GenerationFormV2 — Studio image generation orchestration. @accessibility WCAG 2.1 AA + BFSG konform
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useWorkspaceConfig } from '@/hooks/useWorkspaceConfig';
import type { GenerateImageV2Request } from '@/lib/api/generation';
import { GenerationFormImageUpload } from './GenerationFormImageUpload';
import { GenerationFormPromptSection } from './GenerationFormPromptSection';
import { GenerationFormActions } from './GenerationFormActions';
import { ModelSelector } from './ModelSelector';
import { QualitySelector } from './QualitySelector';
import { useGenerationFileState } from './helpers/useGenerationFileState';
import { useGenerationMutation } from './helpers/useGenerationMutation';
import { useGenerationBrandState } from './helpers/useGenerationBrandState';
import { useGenerationCreditInfo } from './helpers/useGenerationCreditInfo';
import { useGenerationFileHandlers, useIsDragOver } from './helpers/useGenerationFormHandlers';
import { useGenerationSubmit } from './helpers/useGenerationSubmit';
import { useModelSelection } from './helpers/useModelSelection';
import { normalizeImageFormat, normalizePngQuality } from './helpers/normalizeOutputDefaults';
import type { WatermarkPosition } from './VisibleWatermarkSection';

interface FormData { prompt_input: string; ratio: string; resolution: string; preset_type: string; delivery_method: 'wait' | 'mail'; image_count: string; image_format: string; filename_prefix: string; filename_suffix: string; png_quality: string; gen_quality: string; visible_watermark_enabled: boolean; visible_watermark_position: WatermarkPosition; }
export interface ReferenceSlot { id: string | null; previewUrl: string | null; }
export interface GenerationFormV2Props {
  onGenerationStart?: () => void; onSuccess?: () => void; onError?: () => void;
  className?: string; sourceImageId?: string; initialRatio?: string; initialPrompt?: string;
  initialTemplateId?: string; hideImageUpload?: boolean; hideBrandSelector?: boolean;
  externalBrandId?: string | null; externalFile?: File | null; externalPreview?: string | null; hasGeneratedImage?: boolean;
  /** Ref-Bilder aus linkem Panel (StudioSingleShot) */
  externalReferenceSlots?: ReferenceSlot[];
  /** Callback: meldet max_reference_images des aktiven Modells nach oben */
  onMaxReferenceImagesChange?: (n: number) => void;
  /** Pending @REF_N Mention zum Einfügen in den Prompt */
  pendingMention?: string | null;
  onMentionConsumed?: () => void;
}

export function GenerationFormV2({
  onGenerationStart, onSuccess, onError, sourceImageId, initialRatio, initialPrompt,
  initialTemplateId, hideImageUpload = false, hideBrandSelector = false,
  externalBrandId, externalFile, hasGeneratedImage = false,
  externalReferenceSlots, onMaxReferenceImagesChange, pendingMention, onMentionConsumed,
}: GenerationFormV2Props) {
  const { config, isLoading: configLoading } = useWorkspaceConfig();
  const { isDragOver, setIsDragOver } = useIsDragOver();
  const { selectedFile, setSelectedFile, preview, setPreview, productImageLoading, productInfo, clearFile, referencedAssetIdsRef } =
    useGenerationFileState(sourceImageId);
  const { handleFileSelect, handleDrop } = useGenerationFileHandlers({ setSelectedFile, setPreview, setIsDragOver });
  const effectiveFile = hideImageUpload ? externalFile : selectedFile;
  const customPresets = (config?.custom_presets || config?.custom_instructions || []) as Array<{ id: string; name: string; default_brand_id?: string | null }>;
  const cfg = config?.defaults;
  const getMapped = (n?: string) => { if (!n) return 'Packshot'; if (n.startsWith('custom:')) return n; const p = customPresets.find(p => p.name === n); return p ? `custom:${p.id}` : n; };
  const { register, handleSubmit, setValue, watch, getValues, control } = useForm<FormData>({
    values: {
      ratio: initialRatio || cfg?.ratio || '1:1', resolution: '1k',
      preset_type: initialTemplateId ? `custom:${initialTemplateId}` : getMapped(cfg?.preset),
      delivery_method: 'wait', prompt_input: initialPrompt || '', image_count: '1',
      image_format: normalizeImageFormat(cfg?.image_format), filename_prefix: cfg?.filename_prefix || '',
      filename_suffix: cfg?.filename_suffix || '', png_quality: normalizePngQuality(cfg?.png_quality),
      gen_quality: 'medium', visible_watermark_enabled: false, visible_watermark_position: 'bottom-right',
    },
  });
  const [ratio, presetType, resolution, imageCount, imageFormat, pngQuality, genQuality, visibleWatermarkEnabled, visibleWatermarkPosition] = useWatch({
    control, name: ['ratio', 'preset_type', 'resolution', 'image_count', 'image_format', 'png_quality', 'gen_quality', 'visible_watermark_enabled', 'visible_watermark_position'],
  });
  const { selectedModel, handleModelChange, ratioOptions, resolutionOptions } =
    useModelSelection((f, v) => setValue(f, v), ratio, resolution);

  // Pending mention aus linkem Panel in Prompt einfügen
  useEffect(() => {
    if (pendingMention) {
      const current = getValues('prompt_input') ?? '';
      setValue('prompt_input', current ? `${current} ${pendingMention}` : pendingMention);
      onMentionConsumed?.();
    }
  }, [pendingMention, onMentionConsumed, setValue, getValues]);

  // max_reference_images nach oben melden sobald Modell bekannt
  useEffect(() => {
    onMaxReferenceImagesChange?.(selectedModel?.config.max_reference_images ?? 0);
  }, [selectedModel?.config.max_reference_images, onMaxReferenceImagesChange]);
  const { internalBrandId, setInternalBrandId } = useGenerationBrandState({ hideBrandSelector, initialTemplateId, presetType, customPresets });
  const selectedBrandId = (hideBrandSelector ? externalBrandId : internalBrandId) || null;
  const mutation = useGenerationMutation({ onSuccess, onError });
  const { creditsPerImage, cost, credits, hasEnoughCredits, canUseCustomPresets, presetOptions } =
    useGenerationCreditInfo({ config, resolution, imageCount });
  const onSubmit = useGenerationSubmit({
    effectiveFile, creditsPerImage, hasEnoughCredits, cost, credits, selectedBrandId, referencedAssetIdsRef,
    selectedModelCode: selectedModel?.code ?? 'gemini-3-pro-image-preview',
    referenceImageIds: (externalReferenceSlots ?? []).map(s => s.id),
    onGenerationStart, mutate: mutation.mutate as (args: GenerateImageV2Request) => void,
  });
  const promptProps = {
    register, watch, setValue, selectedBrandId, ratio, presetType, resolution, imageCount, imageFormat, pngQuality,
    presetOptions, hideBrandSelector, referencedAssetIdsRef, ratioOptions, resolutionOptions,
    referenceSlots: externalReferenceSlots ?? [],
    visibleWatermarkEnabled, visibleWatermarkPosition,
    onRatioChange: (v: string) => setValue('ratio', v), onPresetChange: (v: string) => setValue('preset_type', v),
    onResolutionChange: (v: string) => setValue('resolution', v), onImageCountChange: (v: string) => setValue('image_count', v),
    onImageFormatChange: (v: string) => setValue('image_format', v), onPngQualityChange: (v: string) => setValue('png_quality', v),
    onVisibleWatermarkEnabledChange: (v: boolean) => setValue('visible_watermark_enabled', v),
    onVisibleWatermarkPositionChange: (v: WatermarkPosition) => setValue('visible_watermark_position', v),
    onBrandChange: setInternalBrandId,
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {!hideImageUpload && (
          <GenerationFormImageUpload
            selectedFile={selectedFile} preview={preview} isDragOver={isDragOver} isLoading={productImageLoading}
            productInfo={productInfo} onFileSelect={handleFileSelect} onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }} onDragLeave={() => setIsDragOver(false)} onClear={clearFile}
          />
        )}
        <ModelSelector selectedModel={selectedModel} onModelChange={handleModelChange} />
        <QualitySelector model={selectedModel} value={genQuality} onChange={(v) => setValue('gen_quality', v)} />
        <GenerationFormPromptSection {...promptProps} />
      </div>
      <div className="flex-shrink-0 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
        <GenerationFormActions
          isSubmitting={mutation.isPending} canSubmit={!mutation.isPending && !!effectiveFile && hasEnoughCredits && !configLoading}
          credits={credits} cost={cost} hasEnoughCredits={hasEnoughCredits}
          canUseCustomPresets={canUseCustomPresets} hasGeneratedImage={hasGeneratedImage}
        />
      </div>
    </form>
  );
}
export default GenerationFormV2;
